import {Cell} from 'react-table';
import {DeprecatedButton, SvgIcon, Tooltip} from '@shipwell/shipwell-ui';
import {validateDateTime} from 'App/utils/dateTimeGlobals';
import {formatDateTime} from 'App/utils/dateTimeGlobalsTyped';
import {Rate} from 'src/@types/quotingTypes';

export interface OnRateSelectChangeEvent {
  (rate: Rate, selected?: boolean): void;
}

export type OnRateStopsUpdateChangeEvent = (rate: Rate) => void;

export type OnRateStopsAcceptChangeEvent = (rate: Rate) => void;

export interface InstantRatesButtonSelectionPropTypes {
  isSelected?: boolean;
  onClick: () => void;
}

export interface InstantRatesUpdateButtonStopsProps {
  onClick: () => void;
}

export interface InstantRatesAcceptRecommendationButtonProps {
  onClick: () => void;
}

export type RateIdType = string | null | undefined;

const InstantRatesButtonSelection = (props: InstantRatesButtonSelectionPropTypes) => {
  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    props.onClick();
  };

  return (
    <DeprecatedButton size="small" onClick={onClickHandler}>
      {props.isSelected ? 'Selected' : 'Select'}
    </DeprecatedButton>
  );
};

const ChangeHereButtonSelection = (props: InstantRatesUpdateButtonStopsProps) => {
  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    props.onClick();
  };

  return (
    <DeprecatedButton size="small" className="float-right" onClick={onClickHandler}>
      {'Change here'}
    </DeprecatedButton>
  );
};

const ChangeAcceptRecommendationButtonSelection = (props: InstantRatesUpdateButtonStopsProps) => {
  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    props.onClick();
  };

  return (
    <DeprecatedButton size="small" className="float-right ml-2" onClick={onClickHandler}>
      {'Accept'}
    </DeprecatedButton>
  );
};

/**
 * Creates table header definitions and behaviors for each row.
 * @param onSelect callback function for when the "select" button is clicked.
 * @param selectedRateId the row to select by rate id
 * @param expandedColumnSet true to show all additional columns, default is false.
 */
const createInstantRatesTableColumns = (
  canTakeActionOnRates: boolean,
  onSelect: OnRateSelectChangeEvent,
  onShowUpdateStops: OnRateStopsUpdateChangeEvent,
  acceptRecommendation: OnRateStopsAcceptChangeEvent,
  selectedRateId?: RateIdType,
  expandedColumnSet?: boolean,
  shouldHideRates = false
) => {
  const FailedMessageTootipComponent = () => (
    <div className="flex items-center justify-center gap-2 text-sw-error">
      <SvgIcon width="20px" height="16px" name="ErrorFilled" color="sw-error" />
      Failed to Quote
    </div>
  );

  const MessageTootipComponent = () => <SvgIcon width="20px" height="20px" name="ErrorOutlined" color="sw-info" />;

  const columns = [
    {
      Header: 'Carrier',
      accessor: 'carrier.displayName',
      width: 100,
      Cell: ({row}: Cell<Rate>) => row?.original?.carrier?.displayName
    },
    {
      Header: 'Pickup',
      accessor: 'pickupDate',
      width: 100,
      Cell: ({row, value}: Cell<Rate, string>) =>
        row.original?.pickupDate && validateDateTime(value) ? formatDateTime(value) : '--'
    },
    ...(expandedColumnSet
      ? [
          {
            Header: 'Delivery',
            accessor: 'deliveryDate',
            width: 100,
            Cell: ({row, value}: Cell<Rate, string>) =>
              row.original?.deliveryDate && validateDateTime(value) ? formatDateTime(value) : '--'
          },
          {
            Header: 'Transit Time',
            accessor: 'transitDays',
            width: 100,
            Cell: ({row, value}: Cell<Rate, string>) => (row.original?.transitDays ? `${value} days` : '--')
          }
        ]
      : []),
    {
      Header: 'Expires',
      accessor: 'expiry',
      width: 100,
      Cell: ({row, value}: Cell<Rate, string>) =>
        row.original?.expirationDate && validateDateTime(value) ? formatDateTime(value) : '--'
    },
    {
      Header: 'Rate',
      accessor: 'rate.amount',
      sortable: true,
      width: 120,
      Cell: function displayRate({row, value}: Cell<Rate, string>) {
        return row.original?.rate?.amount ? (
          <div className="flex flex-row">
            <div className="pr-2 font-bold text-sw-success">{shouldHideRates ? '--' : value}</div>
            <div>
              {!shouldHideRates && row.original.recommendations && row.original.recommendations?.length > 0 ? (
                <Tooltip
                  portal
                  tooltipClassname="w-64"
                  tooltipContent={
                    <div className="font-bold">
                      <ul>
                        {row.original.recommendations &&
                          row.original.recommendations?.map((recommendation, index) => (
                            <li key={index}>- {recommendation.message}</li>
                          ))}
                      </ul>
                      <br />
                      <ChangeAcceptRecommendationButtonSelection onClick={() => acceptRecommendation(row.original)} />
                      <ChangeHereButtonSelection onClick={() => onShowUpdateStops(row.original)} />
                    </div>
                  }
                >
                  <MessageTootipComponent />
                </Tooltip>
              ) : null}
            </div>
          </div>
        ) : (
          <Tooltip
            portal
            tooltipContent={
              <div className="font-bold">
                <ul>
                  {row.original.errors &&
                    row.original.errors?.map((error, index) => (
                      <li key={index}>
                        <div className="w-64">{error}</div>
                      </li>
                    ))}
                </ul>
              </div>
            }
          >
            <FailedMessageTootipComponent />
          </Tooltip>
        );
      }
    },
    ...(canTakeActionOnRates
      ? [
          {
            Header: 'Actions',
            accessor: 'actions',
            width: 100,
            Cell: function displayActions({row}: Cell<Rate, string>) {
              return row.original?.rate?.amount ? (
                <InstantRatesButtonSelection
                  isSelected={selectedRateId === row.original.rateId}
                  onClick={() => onSelect(row.original, !(row.original.rateId === selectedRateId))}
                />
              ) : (
                '--'
              );
            }
          }
        ]
      : [])
  ];

  return Object.values(columns);
};

export {createInstantRatesTableColumns};
